/**
 * Aide contextuelle — Section Assistant Telegram
 *
 * Structure : chaque cle correspond a une route (ou pattern de route).
 * Chaque entree contient :
 *   - title       : Titre affiche en haut du panneau
 *   - user        : Contenu visible par tous (onboarding technicien)
 *   - admin       : Contenu supplementaire visible uniquement par superadmin (logique metier, regles, workflows)
 */

const assistantHelp = {
  // ── Dashboard ──────────────────────────────────────────────────────────────
  '/assistant': {
    title: 'Tableau de bord Assistant',
    user: [
      {
        heading: 'A quoi sert cette page ?',
        content: `Le tableau de bord regroupe l'ensemble des informations captees via le bot Telegram : prospects, idees, rendez-vous et notes. C'est votre point d'entree pour suivre l'activite commerciale et operationnelle au quotidien.`,
      },
      {
        heading: 'Les 4 indicateurs',
        content: `**Prospects actifs** — Fiches prospect en cours de collecte ou en revue.\n**Idees en brouillon** — Idees capturees mais pas encore validees.\n**Rendez-vous a venir** — Prochains rendez-vous planifies.\n**Notes non traitees** — Notes a traiter ou classer.`,
      },
      {
        heading: 'Chronologie recente',
        content: `La timeline affiche les 20 derniers elements crees ou modifies, tous types confondus. Cliquez sur un element pour acceder a son detail.`,
      },
      {
        heading: 'Comment alimenter les donnees ?',
        content: `Toutes les donnees sont captees via le **bot Telegram**. Envoyez un message vocal, texte ou photo au bot. L'IA locale analyse le contenu et le classe automatiquement dans la bonne categorie (prospect, idee, rendez-vous ou note). Vous pouvez aussi creer des entrees manuellement depuis chaque sous-section.`,
      },
    ],
    admin: [
      {
        heading: 'Architecture du systeme',
        content: `**Flux de donnees :** Telegram Bot → n8n (IA locale Llama/Mistral) → API Backend → MongoDB → Frontend React.\n\nLe bot Telegram est un relais sans etat. Toute l'intelligence (detection d'intention, extraction de champs, classification) est executee dans les workflows n8n avec des LLM locaux. Le portail est la couche de stockage et d'affichage.`,
      },
      {
        heading: 'Securite & propriete',
        content: `**Webhook** : Toutes les requetes n8n sont authentifiees par un header \`X-N8N-Secret\` valide contre la collection \`settings\` (cle \`n8n_secret_assistant\`) puis l'env var \`N8N_SECRET_ASSISTANT\`.\n\n**Propriete** : Les prospects, idees et notes sont prives (visibles uniquement par leur proprietaire). Les rendez-vous sont partages entre tous les utilisateurs. Le superadmin voit tout.\n\n**Permissions** : \`assistant.view\` (lecture) et \`assistant.manage\` (CRUD complet).`,
      },
      {
        heading: 'Collections MongoDB',
        content: `\`assistant_prospects\`, \`assistant_ideas\`, \`assistant_appointments\`, \`assistant_notes\`, \`assistant_sessions\` (TTL 24h).\n\nPhotos stockees dans le bucket GridFS \`assistant_blobs\`.`,
      },
      {
        heading: 'Endpoint dashboard',
        content: `\`GET /api/assistant/dashboard\` — Retourne les compteurs, la timeline recente (20 derniers) et les prochains rendez-vous. Filtre par propriete sauf pour les rendez-vous (partages).`,
      },
    ],
  },

  // ── Prospects ──────────────────────────────────────────────────────────────
  '/assistant/prospects': {
    title: 'Prospects',
    user: [
      {
        heading: 'A quoi sert cette page ?',
        content: `Cette page liste toutes les fiches prospect captees par le bot Telegram ou creees manuellement. Un prospect represente un client potentiel avec ses besoins, son parc informatique, ses solutions existantes et les prochaines etapes commerciales.`,
      },
      {
        heading: 'Statuts d\'un prospect',
        content: `**En collecte** (bleu) — Le bot est en train de rassembler les informations. Chaque message enrichit la fiche.\n**En revue** (orange) — La collecte est terminee, la fiche est a valider par un technicien.\n**Termine** (vert) — La fiche est validee et exploitable.\n**Archive** (gris) — La fiche n'est plus active.`,
      },
      {
        heading: 'Generation de PDF',
        content: `Depuis le detail d'un prospect, vous pouvez generer deux types de PDF :\n- **PDF interne** : Contient toutes les informations, y compris les messages bruts Telegram, l'analyse IA et les photos.\n- **PDF client** : Version professionnelle epuree, sans les notes internes ni les messages bruts. Pret a envoyer au client.`,
      },
      {
        heading: 'Photos',
        content: `Les photos envoyees au bot Telegram (ex: photo de la salle serveur, du reseau) sont automatiquement rattachees au prospect en cours de collecte et stockees dans le portail.`,
      },
    ],
    admin: [
      {
        heading: 'Workflow de collecte Telegram',
        content: `1. L'utilisateur envoie un message au bot : "Je visite Acme Corp, 50 postes, besoin licences ESET"\n2. n8n detecte l'intention "prospect" via LLM local\n3. \`POST /api/n8n/webhook/assistant/ingest\` cree la fiche (status=collecting)\n4. Messages suivants : \`PATCH /api/n8n/webhook/assistant/update/prospect/{id}\` enrichit la fiche\n5. Photos : \`POST /api/n8n/webhook/assistant/photos\` stocke en GridFS\n6. "c'est bon" / "recap" → n8n genere ai_summary + ai_gaps → status passe en "review"`,
      },
      {
        heading: 'Auto-reclassification',
        content: `Quand n8n classe un message comme "note", le backend verifie automatiquement si ca ressemble a un prospect (mots-cles : prospect, client potentiel, devis, commercial, parc informatique, budget). Si oui, il reclasse automatiquement en prospect. Meme logique pour les rendez-vous et les idees.`,
      },
      {
        heading: 'Transitions de statut',
        content: `collecting → review → completed → archived\n\nLes transitions sont validees cote backend. Une fiche archivee ne peut plus etre modifiee (retour 409 Conflict).`,
      },
      {
        heading: 'Schema prospect',
        content: `Champs principaux : \`company_name\`, \`contact\` {name, phone, email, role}, \`address\`, \`it_park\` {workstations, servers, printers, network_type}, \`expressed_needs\` [{description, priority}], \`existing_solutions\` [{category, product, notes}], \`detected_priorities\`, \`budget_notes\`, \`next_steps\` [{action, date, notes}], \`ai_gaps\`, \`ai_summary\`, \`photos\` [{blob_id, description}], \`raw_messages\` (max 200), \`tags\`.`,
      },
      {
        heading: 'PDF — implementation',
        content: `Genere par WeasyPrint + templates Jinja2 (\`prospect_pdf_internal.html\`, \`prospect_pdf_client.html\`). Photos embarquees en base64 dans le PDF interne uniquement. Le PDF client exclut : raw_messages, ai_gaps, detected_priorities, photos.`,
      },
      {
        heading: 'Suppression',
        content: `La suppression d'un prospect supprime egalement toutes ses photos dans GridFS (cascade).`,
      },
    ],
  },

  // ── Prospect detail ────────────────────────────────────────────────────────
  '/assistant/prospects/:id': {
    title: 'Detail prospect',
    user: [
      {
        heading: 'Navigation',
        content: `Cette page affiche le detail complet d'un prospect. Les informations sont organisees en sections repliables : contact, parc IT, besoins exprimes, solutions existantes, priorites detectees, prochaines etapes.`,
      },
      {
        heading: 'Modifier la fiche',
        content: `Cliquez sur le bouton "Modifier" pour editer les champs. Les modifications sont enregistrees instantanement. Vous pouvez ajouter des tags pour organiser vos prospects.`,
      },
      {
        heading: 'Messages bruts',
        content: `Le bloc "Messages bruts" en bas affiche les messages Telegram originaux qui ont servi a construire la fiche. C'est utile pour retrouver le contexte exact d'un echange.`,
      },
    ],
    admin: [
      {
        heading: 'Analyse IA',
        content: `Le champ \`ai_summary\` contient le resume structure genere par le LLM local. \`ai_gaps\` liste les informations manquantes detectees par l'IA (ex: "email du contact non fourni"). Ces champs sont generes par n8n et ne sont pas recalcules cote portail.`,
      },
      {
        heading: 'Raw messages',
        content: `Chaque message contient : \`type\` (text/voice/photo), \`content\` (texte ou transcription), \`timestamp\`. Plafonne a 200 messages par entite — les plus anciens sont supprimes automatiquement.`,
      },
    ],
  },

  // ── Idees ──────────────────────────────────────────────────────────────────
  '/assistant/ideas': {
    title: 'Idees',
    user: [
      {
        heading: 'A quoi sert cette page ?',
        content: `Centralisez ici toutes les idees d'amelioration capturees au quotidien : nouvelles fonctionnalites, ameliorations de processus, optimisations. Chaque idee est classee par type et par projet cible.`,
      },
      {
        heading: 'Types d\'idees',
        content: `**Fonctionnalite** — Nouvelle feature pour un produit/projet.\n**Processus** — Amelioration d'un workflow ou d'une methode de travail.\n**Autre** — Tout le reste.`,
      },
      {
        heading: 'Statuts',
        content: `**Brouillon** (indigo) — Idee capturee, a affiner.\n**Validee** (vert) — Idee jugee pertinente.\n**Planifiee** (bleu) — Integree dans un planning de realisation.\n**Archivee** (gris) — Idee abandonnee ou realisee.`,
      },
    ],
    admin: [
      {
        heading: 'Transitions de statut',
        content: `draft → validated → planned → archived. Validees cote backend.`,
      },
      {
        heading: 'Template fields',
        content: `Le champ \`template_fields\` est un objet dynamique dont le contenu varie selon \`idea_type\`. Permet d'ajouter des champs specifiques par type d'idee sans modifier le schema.`,
      },
      {
        heading: 'Auto-reclassification',
        content: `Si n8n classe un message comme "note" mais qu'il contient des mots-cles d'idee (idee, fonctionnalite, feature, amelioration, processus, workflow), il est automatiquement reclasse en "idea".`,
      },
    ],
  },

  '/assistant/ideas/:id': {
    title: 'Detail idee',
    user: [
      {
        heading: 'Modifier une idee',
        content: `Editez le titre, la description, le type et le projet cible. Ajoutez des tags pour organiser. Le changement de statut se fait via le selecteur de statut en haut de page.`,
      },
    ],
    admin: [
      {
        heading: 'Champs stockes',
        content: `\`idea_type\` (feature/process/other), \`target_project\`, \`title\`, \`description\`, \`template_fields\` (dynamique), \`raw_messages\`, \`tags\`.`,
      },
    ],
  },

  // ── Rendez-vous ────────────────────────────────────────────────────────────
  '/assistant/appointments': {
    title: 'Rendez-vous',
    user: [
      {
        heading: 'A quoi sert cette page ?',
        content: `Tous les rendez-vous planifies, qu'ils soient captures via Telegram ou crees manuellement. Les rendez-vous sont partages entre tous les utilisateurs du portail (contrairement aux prospects et notes qui sont prives).`,
      },
      {
        heading: 'Creation via Telegram',
        content: `Envoyez un message du type : "RDV lundi 14h30 avec Jean Dupont chez client a Paris". Le systeme extrait automatiquement la date, l'heure, la personne et le lieu.`,
      },
      {
        heading: 'Statuts',
        content: `**En attente** (orange) — Rendez-vous planifie, pas encore confirme.\n**Confirme** (bleu) — Rendez-vous valide.\n**Effectue** (vert) — Rendez-vous passe.\n**Annule** (rouge) — Rendez-vous annule.`,
      },
      {
        heading: 'Popups de rappel',
        content: `Pour chaque RDV (ou intervention de ticket), jusqu'a **2 popups** peuvent s'afficher :\n\n**Popup vert "Imminent"** — 15 min avant l'heure du RDV.\n**Popup rouge "En retard"** — au moment ou le RDV passe son heure si toujours actif.\n\nUne alerte de rappel **1h avant** est aussi creee mais elle s'affiche uniquement dans la cloche de notifications (pas en popup).\n\nDans le popup :\n- **Silence...** (5/15/30 min, 1h, cet apres-midi, **ce soir (18 h)**, demain) — cache le popup, il revient au moment voulu. **Ce soir** = 18 h le jour meme (apres la fermeture et l'heure de gestion) ; l'option disparait une fois 18 h passees.\n- **X (fermer)** — silence definitif pour ce RDV.\n- **Voir le RDV / Voir le ticket** — ouvre la fiche (snooze 15 min en parallele). Pour une tache, la fiche du ticket se deroule jusqu'a la tache, depliee et surlignee (idem depuis la cloche).\n\n**Synchronisation entre onglets** : un snooze ou un X sur **un onglet** se propage immediatement aux **autres onglets ouverts** (et au plus 30s sur d'autres appareils). Idem pour les popups frere du meme RDV : si tu snooze le rouge, le vert sibling est aussi silence.`,
      },
    ],
    admin: [
      {
        heading: 'Extraction de date en francais',
        content: `Le module \`assistant_date_parser.py\` parse le texte en francais pour extraire :\n- **Date** : "aujourd'hui", "demain", "apres-demain", jours de la semaine, "le 25 mars", "25/03/2026"\n- **Heure** : "14h30", "14:30", "9h"\n- **Personne** : patterns "avec Jean Dupont", "chez Societe X"\n- **Lieu** : patterns "a Paris", "chez client"\n\nSi la date extraite est dans le passe (sans annee specifiee), le systeme assume l'annee suivante.`,
      },
      {
        heading: 'Visibilite partagee',
        content: `Les rendez-vous n'ont **pas de champ owner_id** — ils sont visibles par tous les utilisateurs ayant la permission \`assistant.view\`. C'est une decision de design : les rendez-vous sont partages pour la coordination d'equipe.`,
      },
      {
        heading: 'Rappels — implementation',
        content: `Geres par le portail dans \`appointment_reminder_svc.py\` (cron 60s).\n\n**Offsets** : champ \`reminder_offsets\` sur l'appointment (defaut: \`[60, 15]\`). Pour chaque offset, une notif \`appointment_reminder\` est creee dans \`col_notifications\` avec \`severity: critical\` si offset <= 15, sinon \`warning\`. \`reminder_sent.{offset}\` evite les doublons.\n\n**Overdue** : separement, \`check_overdue_appointments()\` cree une notif \`severity: critical, overdue: true\` si le RDV est passe et toujours pending/confirmed (lookback 7 jours).\n\n**Snooze/dismiss per-user** : champs \`snoozed_by: [{email, until}]\` et \`dismissed_by: [email]\` sur la notif. \`POST /api/notifications/{id}/snooze\` body \`{minutes}\` et \`POST /api/notifications/action\` action \`dismiss\`.\n\n**Groupage** : un snooze/dismiss sur une notif d'un RDV/intervention propage a TOUTES les notifs \`appointment_reminder\` du meme \`appointment_id\` ou \`ticket_id\` via \`_reminder_group_query()\`.\n\n**Cross-tab** : composant \`AppointmentReminderPopup\` poll \`/api/notifications?status=pending\` toutes les 30s + \`BroadcastChannel('reminder_sync')\` pour propager les fermetures instantanement entre onglets du meme navigateur.`,
      },
      {
        heading: 'Transitions de statut',
        content: `pending ↔ confirmed → done | cancelled. On peut aller-retour entre pending et confirmed. Une fois done ou cancelled, c'est terminal.`,
      },
    ],
  },

  '/assistant/appointments/:id': {
    title: 'Detail rendez-vous',
    user: [
      {
        heading: 'Informations affichees',
        content: `Date et heure, duree, personne rencontree, lieu, contexte. Modifiez directement depuis cette page.`,
      },
    ],
    admin: [
      {
        heading: 'Champs stockes',
        content: `\`title\`, \`datetime\` (ISO 8601), \`duration_minutes\` (defaut 60), \`context\`, \`person\` {name, company, phone, email}, \`person_name\` (doublon pour binding formulaire), \`location\`, \`reminder_offsets\` [60, 15], \`calendar_synced\`, \`calendar_event_id\`.`,
      },
    ],
  },

  // ── Notes ──────────────────────────────────────────────────────────────────
  '/assistant/notes': {
    title: 'Notes',
    user: [
      {
        heading: 'A quoi sert cette page ?',
        content: `Les notes sont un fourre-tout pour tout ce qui ne rentre pas dans les autres categories. Quand le bot Telegram ne detecte ni un prospect, ni une idee, ni un rendez-vous, le message est classe comme note.`,
      },
      {
        heading: 'Statuts',
        content: `**Nouveau** (indigo) — Note fraichement capturee.\n**Traitee** (vert) — Note lue et prise en compte.\n**Archivee** (gris) — Note classee.`,
      },
      {
        heading: 'Categories',
        content: `Le bot Telegram suggere une categorie automatiquement (ex: "technique", "administratif", "idee"). Vous pouvez la modifier manuellement.`,
      },
    ],
    admin: [
      {
        heading: 'Auto-reclassification',
        content: `Quand un message arrive avec type="note", le backend verifie dans cet ordre :\n1. Contient des mots-cles de rendez-vous + une date/heure → reclasse en "appointment"\n2. Contient des mots-cles prospect → reclasse en "prospect"\n3. Contient des mots-cles idee → reclasse en "idea"\n4. Sinon reste "note"\n\nMots-cles rendez-vous : rdv, rendez-vous, rappel, planifi, reunion, meeting, call, visio + noms de jours + patterns de date/heure.\nMots-cles prospect : prospect, client potentiel, devis, commercial, entreprise, parc informatique, besoin, budget.\nMots-cles idee : idee, fonctionnalite, feature, amelioration, processus, workflow.`,
      },
      {
        heading: 'Transitions de statut',
        content: `new → processed → archived.`,
      },
    ],
  },

  '/assistant/notes/:id': {
    title: 'Detail note',
    user: [
      {
        heading: 'Modifier une note',
        content: `Editez le titre, la categorie et le contenu. Les messages Telegram originaux sont affiches en bas de page.`,
      },
    ],
    admin: [
      {
        heading: 'Champs stockes',
        content: `\`category\` (libre, suggeree par LLM), \`title\` (auto-genere par LLM), \`content\` (structure par LLM), \`raw_messages\`, \`tags\`.`,
      },
    ],
  },

  // ── KB Brouillons ──────────────────────────────────────────────────────────
  '/assistant/kb-drafts': {
    title: 'KB Brouillons',
    user: [
      {
        heading: 'A quoi sert cette page ?',
        content: `Les brouillons KB sont des articles de base de connaissances generes automatiquement par le bot Telegram. Quand vous envoyez au bot une procedure technique, un tuto ou une solution a un probleme, il cree un brouillon dans la KB.`,
      },
      {
        heading: 'Workflow',
        content: `1. Envoyez votre contenu technique au bot Telegram.\n2. Le bot le structure en article KB (titre, contenu, tags).\n3. L'article apparait ici en statut "Brouillon".\n4. Cliquez dessus pour l'ouvrir dans l'editeur KB, le relire et le publier.`,
      },
    ],
    admin: [
      {
        heading: 'Implementation technique',
        content: `Les brouillons KB ne sont pas stockes dans les collections \`assistant_*\` mais dans \`kb_articles\` avec \`source: "telegram_assistant"\` et \`status: "draft"\`.\n\nLa liste est fetched via \`GET /api/kb?source=telegram_assistant&status=draft\`.\n\nAu clic, redirection vers l'editeur KB existant (\`KbEditorPage\`). Aucun editeur specifique n'est necessaire.`,
      },
      {
        heading: 'Ingest webhook',
        content: `Quand n8n envoie un ingest avec \`type: "kb_draft"\`, le backend cree un document dans \`kb_articles\` (pas \`assistant_notes\`) via \`build_kb_draft_doc()\`. Le document recoit automatiquement \`source: "telegram_assistant"\`.`,
      },
    ],
  },
};

export default assistantHelp;
